import { useEffect, useReducer } from "react";

import { Classes } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import {
  AcceptingClientsQuery,
  useAcceptingClientsQuery,
  useUpdateCoachProfileMutation,
} from "@/graphql";

type AcceptingActionType = "SET_NEW_CLIENTS" | "SET_NEW_BOOKINGS" | "INIT";

type Profile = NonNullable<
  NonNullable<AcceptingClientsQuery["currentUser"]>["agentProfile"]
>;

interface AcceptingAction {
  type: AcceptingActionType;
  value: boolean | Profile;
}

interface AcceptingState {
  ready: boolean;
  acceptingNewClients: boolean;
  acceptingNewBookings: boolean;
}

function reducer(state: AcceptingState, action: AcceptingAction) {
  switch (action.type) {
    case "SET_NEW_CLIENTS":
      return {
        ...state,
        acceptingNewClients: action.value as boolean,
        acceptingNewBookings:
          (action.value as boolean) || state.acceptingNewBookings,
      };
    case "SET_NEW_BOOKINGS":
      return {
        ...state,
        acceptingNewBookings: action.value as boolean,
      };
    case "INIT":
      if (action.value && typeof action.value === "object") {
        const profile = action.value;
        return {
          ready: true,
          acceptingNewClients: !!profile.acceptingNewClients,
          acceptingNewBookings: !!profile.acceptingNewBookings,
        };
      }
      return state;
    default:
      console.log("Unknown action: " + action.type);
      return state;
  }
}

export function AcceptingClients() {
  const [state, dispatch] = useReducer(reducer, {
    ready: false,
    acceptingNewClients: false,
    acceptingNewBookings: false,
  });

  const { data, loading } = useAcceptingClientsQuery();
  const [mutate] = useUpdateCoachProfileMutation();

  useEffect(() => {
    if (data?.currentUser?.agentProfile) {
      dispatch({
        type: "INIT",
        value: data.currentUser.agentProfile,
      });
    }
  }, [data]);

  useEffect(() => {
    if (!state.ready) return;
    mutate({
      variables: {
        acceptingNewClients: state.acceptingNewClients,
        acceptingNewBookings: state.acceptingNewBookings,
      },
      refetchQueries: ["AcceptingClients"],
    });
  }, [state]);

  const isXs = useMediaQuery(isXsQuery);

  return (
    <Box
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      py={[3, 3, 3, 24]}
      px={[3, 3, 3, 4]}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <Box fontSize={20} fontWeight={600}>
            New Clients
          </Box>
          <Box
            className={loading ? Classes.SKELETON : ""}
            color={SUBTEXT_COLOR}
          >
            {state.acceptingNewClients ? (
              <>
                You <b>are</b> currently accepting new clients
              </>
            ) : (
              <>
                You <b>are not</b> currently accepting new clients
              </>
            )}
          </Box>
        </Box>
        <Box>
          {loading || state.acceptingNewClients === null ? (
            <Box className={Classes.SKELETON} height={26} width={46} />
          ) : (
            <ToggleField
              checked={state.acceptingNewClients}
              onChange={(newVal) =>
                dispatch({
                  type: "SET_NEW_CLIENTS",
                  value: newVal,
                })
              }
            />
          )}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" mt={24}>
        <Box>
          <Box fontSize={20} fontWeight={600}>
            New Bookings
          </Box>
          <Box
            className={loading ? Classes.SKELETON : ""}
            color={SUBTEXT_COLOR}
            maxWidth={isXs ? 280 : undefined}
          >
            {state.acceptingNewBookings ? (
              <>
                You <b>are</b> currently accepting new sessions and reviews from
                existing clients
              </>
            ) : (
              <>
                You <b>are not</b> currently accepting new sessions and reviews
                from existing clients
              </>
            )}
          </Box>
        </Box>
        <Box>
          {loading || state.acceptingNewBookings === null ? (
            <Box className={Classes.SKELETON} height={26} width={46} />
          ) : (
            <Tooltip
              content={
                <Box maxWidth={200}>
                  If you are accepting new clients you must also accept new
                  bookings
                </Box>
              }
              disabled={!state.acceptingNewClients}
            >
              <ToggleField
                checked={state.acceptingNewBookings}
                disabled={state.acceptingNewClients}
                onChange={(newVal) =>
                  dispatch({
                    type: "SET_NEW_BOOKINGS",
                    value: newVal,
                  })
                }
              />
            </Tooltip>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
